import React, { createContext, useMemo, useState } from 'react';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import {
  JobDto,
  JobSummaryDto2,
  JobUserDto,
  MeetingTypeDto,
  UserDto,
} from 'src/api/FiretrolApiClient';
import { useJob } from 'src/api/queries/jobs/useJob';
import { useJobsList } from 'src/api/queries/jobs/useJobsList';
import { useJobSummary } from 'src/api/queries/jobs/useJobSummary';
import { useJobTeam } from 'src/api/queries/jobs/useJobTeam';
import { useJobUsers } from 'src/api/queries/jobs/useJobUsers';
import { useMeetingTypes } from 'src/api/queries/jobs/useMeetingTypes';
import { queryKeys } from 'src/api/queries/queryKeys';
import { PropsWithChildren } from 'src/PropsWithChildren';

interface IJobsContext {
  jobs: JobDto[];
  team: JobUserDto[];
  teamV2: JobUserDto[];
  users: UserDto[];
  jobSummary: JobSummaryDto2;
  meetingTypes: MeetingTypeDto[];
  selectedJob: JobDto | null;
  setSelectedJob: (job: JobDto | null) => void;
  setMeetingTypes: (meetingTypes: MeetingTypeDto[]) => void;
  loading: boolean;
  loadSummary: () => Promise<void>;
  rehydrateJob: () => Promise<void>;
  fetchJobs: () => Promise<void>;
  fetchTeam: () => Promise<void>;
  refreshTeam: () => Promise<void>;
  refreshMeetingTypes: () => Promise<void>;
  loadingTeam: boolean;
}

const EMPTY_SUMMARY: JobSummaryDto2 = {
  ctcJob: undefined,
  constraints: [],
  due: [],
  milestones: [],
};

const EMPTY_JOBS: JobDto[] = [];
const EMPTY_TEAM: JobUserDto[] = [];
const EMPTY_USERS: UserDto[] = [];
const EMPTY_MEETING_TYPES: MeetingTypeDto[] = [];

export const JobsContext = createContext<IJobsContext>({
  jobs: EMPTY_JOBS,
  team: EMPTY_TEAM,
  teamV2: EMPTY_TEAM,
  users: EMPTY_USERS,
  meetingTypes: EMPTY_MEETING_TYPES,
  jobSummary: EMPTY_SUMMARY,
  selectedJob: null,
  setSelectedJob: () => null,
  setMeetingTypes: () => null,
  loading: true,
  loadingTeam: false,
  loadSummary: () => Promise.resolve(),
  fetchJobs: () => Promise.resolve(),
  fetchTeam: () => Promise.resolve(),
  refreshTeam: () => Promise.resolve(),
  refreshMeetingTypes: () => Promise.resolve(),
  rehydrateJob: () => Promise.resolve(),
});

/**
 * Adapter over the job query cache. It owns only the id of the job the user is
 * looking at; every slice below is a live query, so any mutation that
 * invalidates a `queryKeys.jobs.*` key refreshes this context too.
 */
export const JobsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const qc = useQueryClient();
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  const jobsQuery = useJobsList();
  const jobQuery = useJob(selectedJobId);
  const teamQuery = useJobTeam(selectedJobId);
  const usersQuery = useJobUsers(selectedJobId);
  const summaryQuery = useJobSummary(selectedJobId);
  const meetingTypesQuery = useMeetingTypes(selectedJobId);

  const value = useMemo<IJobsContext>(() => {
    const invalidate = async (queryKey: QueryKey) => {
      await qc.invalidateQueries({ queryKey });
    };
    const team = teamQuery.data ?? EMPTY_TEAM;
    const refreshTeamAndUsers = async () => {
      if (selectedJobId == null) return;
      await Promise.all([
        invalidate(queryKeys.jobs.team(selectedJobId)),
        invalidate(queryKeys.jobs.users(selectedJobId)),
      ]);
    };

    return {
      jobs: jobsQuery.data ?? EMPTY_JOBS,
      team,
      teamV2: team,
      users: usersQuery.data ?? EMPTY_USERS,
      jobSummary: summaryQuery.data ?? EMPTY_SUMMARY,
      meetingTypes: meetingTypesQuery.data ?? EMPTY_MEETING_TYPES,
      selectedJob: jobQuery.data ?? null,
      loading: jobsQuery.isLoading,
      loadingTeam: teamQuery.isLoading || usersQuery.isLoading,
      setSelectedJob: (job: JobDto | null) => {
        // Callers pass a job from the list endpoint, which carries no team,
        // rates or workflow objects, so it may only stand in until the detail
        // query loads — never over an already-cached detail.
        if (
          job &&
          qc.getQueryData(queryKeys.jobs.detail(job.id)) === undefined
        ) {
          qc.setQueryData(queryKeys.jobs.detail(job.id), job);
        }
        setSelectedJobId(job?.id ?? null);
      },
      setMeetingTypes: (meetingTypes: MeetingTypeDto[]) => {
        if (selectedJobId == null) return;
        qc.setQueryData(
          queryKeys.jobs.meetingTypes(selectedJobId),
          meetingTypes,
        );
      },
      fetchJobs: () => invalidate(queryKeys.jobs.list()),
      rehydrateJob: () =>
        selectedJobId == null
          ? Promise.resolve()
          : invalidate(queryKeys.jobs.detail(selectedJobId)),
      loadSummary: () =>
        selectedJobId == null
          ? Promise.resolve()
          : invalidate(queryKeys.jobs.summary(selectedJobId)),
      refreshMeetingTypes: () =>
        selectedJobId == null
          ? Promise.resolve()
          : invalidate(queryKeys.jobs.meetingTypes(selectedJobId)),
      fetchTeam: refreshTeamAndUsers,
      refreshTeam: refreshTeamAndUsers,
    };
  }, [
    qc,
    selectedJobId,
    jobsQuery.data,
    jobsQuery.isLoading,
    jobQuery.data,
    teamQuery.data,
    teamQuery.isLoading,
    usersQuery.data,
    usersQuery.isLoading,
    summaryQuery.data,
    meetingTypesQuery.data,
  ]);

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
};

export const useJobs: () => IJobsContext = () => React.useContext(JobsContext);
